import Cookies from 'js-cookie';
import { NavigateFunction } from 'react-router-dom';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { AdminApiService } from 'admin/AdminApiService';
import {
   CompanySlide,
   DeactivatesliderResponse,
   SelectForSliderResponse,
   UpdateEmployerOrderDto,
   UpdateEmployerOrderResponse,
} from 'admin/types';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { AsyncState } from 'shared/libs/state';
import { PaginationControl } from 'shared/components/pagination';

type SliderSort = keyof Pick<
   CompanySlide,
   'company_name' | 'employer_id' | 'email_address' | 'home_slider_created'
>;
type SortDirection = 'asc' | 'desc';

export class SliderManagerStore {
   rawSliders: CompanySlide[] = [];
   searchStr: string = '';
   dateFrom = '';
   dateTo = '';
   sorter: SliderSort = 'company_name';
   direction: SortDirection = 'asc';

   private readonly PER_PAGE = 10;
   readonly loadSlidersState = new AsyncState<SelectForSliderResponse>();
   readonly updateSliderState = new AsyncState<UpdateEmployerOrderResponse>();
   readonly deactivateSliderState = new AsyncState<DeactivatesliderResponse>();
   readonly pagination = new PaginationControl();

   readonly draftFilters = observable.object({
      searchStr: '',
      dateFrom: '',
      dateTo: '',
   });

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawSliders: observable,
         sorter: observable,
         searchStr: observable,
         dateFrom: observable,
         dateTo: observable,
         direction: observable,
         sliders: computed,
         filteredSliders: computed,
         totalPages: computed,
         onSearchChange: action.bound,
         onSortBy: action.bound,
         onDateFromChange: action.bound,
         onDateToChange: action.bound,
         onApplyFilters: action.bound,
      });
   }

   get totalPages(): number {
      return Math.ceil(this.filteredSliders.length / this.PER_PAGE);
   }

   get filteredSliders(): CompanySlide[] {
      const search = this.searchStr.trim().toLocaleLowerCase();
      const filteredSliders = this.rawSliders.filter((slide) => {
         const matchesSearch =
            !search ||
            slide.company_name?.toLowerCase().includes(search) ||
            slide.employer_id?.toString().toLowerCase().includes(search) ||
            slide.email_address?.toLowerCase().includes(search);

         if (!matchesSearch) return false;

         const created = slide.home_slider_created;

         const afterFrom = !this.dateFrom || DateUtilsService.checkIsAfter(created, this.dateFrom);

         const beforeTo = !this.dateTo || DateUtilsService.checkIsBefore(created, this.dateTo);

         return afterFrom && beforeTo;
      });

      return filteredSliders.sort(this._getSortCallback());
   }

   get sliders(): CompanySlide[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.filteredSliders.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   onSortBy(sort: SliderSort): void {
      if (this.sorter === sort) {
         if (this.direction === 'asc') {
            this.direction = 'desc';
         } else {
            this.direction = 'asc';
         }
      } else {
         this.sorter = sort;
         if (this.direction === 'asc') {
            this.direction = 'desc';
         } else {
            this.direction = 'asc';
         }
      }
   }

   onSearchChange(value: string): void {
      this.draftFilters.searchStr = value;
   }

   onDateFromChange(value: string): void {
      this.draftFilters.dateFrom = value;
   }

   onDateToChange(value: string): void {
      this.draftFilters.dateTo = value;
   }

   onApplyFilters(): void {
      this.searchStr = this.draftFilters.searchStr;
      this.dateFrom = this.draftFilters.dateFrom;
      this.dateTo = this.draftFilters.dateTo;
      this.pagination.setPage(0);
   }

   private _getSortCallback() {
      if (this.sorter === 'home_slider_created') {
         return (a: CompanySlide, b: CompanySlide) =>
            DateUtilsService.compare(this.direction, a.home_slider_created, b.home_slider_created);
      }

      return (a: CompanySlide, b: CompanySlide) => {
         if (a[this.sorter] < b[this.sorter]) {
            return this.direction === 'asc' ? -1 : 1;
         }
         if (a[this.sorter] > b[this.sorter]) {
            return this.direction === 'asc' ? 1 : -1;
         }
         return 0;
      };
   }

   async loadSlider() {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.loadSlidersState.run(
         () =>
            AdminApiService.selectforslider(null, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               runInAction(() => {
                  this.rawSliders = resp.response.map((slider) => ({
                     ...slider,
                     home_slider_created: DateUtilsService.toAppFormat(slider.home_slider_created),
                  }));
               });
            },
         }
      );
   }

   async updateOrder({
      dto,
      onSuccess,
      onError,
   }: {
      dto: UpdateEmployerOrderDto;
      onSuccess: () => void;
      onError: () => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }
      await this.updateSliderState.run(
         () =>
            AdminApiService.updateemployerorder(dto, {
               tokenKey,
               adminID,
            }),
         { onSuccess, onError }
      );
   }

   async deactivateSlider({
      dto,
      onSuccess,
      onError,
   }: {
      dto: { slug: string };
      onSuccess: (resp: DeactivatesliderResponse) => void;
      onError: () => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deactivateSliderState.run(
         () =>
            AdminApiService.deactivateslider(dto, {
               tokenKey,
               adminID,
            }),
         { onSuccess, onError }
      );
   }
}
